/**
 * Create Booking Dialog Component
 * Form for creating new bookings
 */
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, UserPlus, MapPin, Calendar, DollarSign, Percent, Tag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CreateBookingDialog({
  open,
  onOpenChange,
  onBookingCreated,
  packages = [],
  addons = [],
  eventTypes = [],
  venues = [],
  discounts = [],
  settings,
  user
}) {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    event_type: '',
    event_date: '',
    start_time: '',
    end_time: '',
    venue_id: '',
    venue_name: '',
    venue_address: '',
    package_id: '',
    selected_addons: [],
    notes: '',
    status: 'inquiry',
    payment_type: 'deposit',
    deposit_amount: '',
    payment_schedule: []
  });

  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const clientSearchRef = useRef(null);

  // Search clients
  useEffect(() => {
    const searchClients = async () => {
      if (clientSearchQuery.length < 2) {
        setClientSearchResults([]);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await axios.get(`${API}/clients/search?q=${encodeURIComponent(clientSearchQuery)}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        setClientSearchResults(response.data);
        setShowClientDropdown(true);
      } catch (error) {
        console.error('Error searching clients:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchClients, 300);
    return () => clearTimeout(debounce);
  }, [clientSearchQuery]);

  const selectClient = (client) => {
    setFormData({
      ...formData,
      client_name: client.name,
      client_email: client.email || '',
      client_phone: client.phone || ''
    });
    setClientSearchQuery(client.name);
    setShowClientDropdown(false);
  };

  const selectVenue = (venue) => {
    setFormData({
      ...formData,
      venue_id: venue.id,
      venue_name: venue.name,
      venue_address: venue.address || ''
    });
  };

  const calculateTotal = () => {
    let total = 0;
    
    // Package price
    const selectedPackage = packages.find(p => p.id === formData.package_id);
    if (selectedPackage) {
      total += selectedPackage.price || 0;
    }
    
    // Add-ons
    formData.selected_addons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) {
        total += addon.price || 0;
      }
    });
    
    return total;
  };

  const handleSubmit = async () => {
    if (!formData.client_name || !formData.event_date) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const total = calculateTotal();
      
      await axios.post(`${API}/bookings`, {
        ...formData,
        total_price: total
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Booking created!');
      onOpenChange(false);
      onBookingCreated();
      
      // Reset form
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        event_type: '',
        event_date: '',
        start_time: '',
        end_time: '',
        venue_id: '',
        venue_name: '',
        venue_address: '',
        package_id: '',
        selected_addons: [],
        notes: '',
        status: 'inquiry',
        payment_type: 'deposit',
        deposit_amount: '',
        payment_schedule: []
      });
      setClientSearchQuery('');
    } catch (error) {
      toast.error('Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  const selectedPackage = packages.find(p => p.id === formData.package_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-neon-blue hover:bg-neon-blue/90 text-black">
          <Plus className="w-4 h-4 mr-2" />
          New Booking
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-foreground">
            Create New Booking
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Client Info */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Client Information
            </h3>
            
            <div className="relative" ref={clientSearchRef}>
              <Label>Client Name *</Label>
              <Input
                value={clientSearchQuery}
                onChange={(e) => {
                  setClientSearchQuery(e.target.value);
                  setFormData({ ...formData, client_name: e.target.value });
                }}
                placeholder="Search or enter client name"
                className="bg-muted border-border"
              />
              {showClientDropdown && clientSearchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {clientSearchResults.map(client => (
                    <button
                      key={client.id}
                      onClick={() => selectClient(client)}
                      className="w-full px-4 py-2 text-left hover:bg-muted/50 flex items-center gap-2"
                    >
                      <span className="font-medium">{client.name}</span>
                      {client.email && <span className="text-sm text-muted-foreground">{client.email}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  placeholder="client@email.com"
                  className="bg-muted border-border"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-muted border-border"
                />
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Event Details
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Type *</Label>
                <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                  <SelectTrigger className="bg-muted border-border">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map(type => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Event Date *</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                  className="bg-muted border-border"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="bg-muted border-border"
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="bg-muted border-border"
                />
              </div>
            </div>
          </div>

          {/* Package Selection */}
          <div className="space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Package
            </h3>

            <Select value={formData.package_id} onValueChange={(v) => setFormData({ ...formData, package_id: v })}>
              <SelectTrigger className="bg-muted border-border">
                <SelectValue placeholder="Select a package" />
              </SelectTrigger>
              <SelectContent>
                {packages.map(pkg => (
                  <SelectItem key={pkg.id} value={pkg.id}>
                    {pkg.name} - ${pkg.price?.toFixed(2)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add-ons */}
          {addons.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold">Add-ons</h3>
              <div className="grid grid-cols-2 gap-2">
                {addons.filter(a => a.is_active).map(addon => (
                  <label key={addon.id} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50">
                    <Checkbox
                      checked={formData.selected_addons.includes(addon.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setFormData({ ...formData, selected_addons: [...formData.selected_addons, addon.id] });
                        } else {
                          setFormData({ ...formData, selected_addons: formData.selected_addons.filter(id => id !== addon.id) });
                        }
                      }}
                    />
                    <span className="flex-1">{addon.name}</span>
                    <span className="text-muted-foreground">${addon.price?.toFixed(2)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              className="bg-muted border-border"
              rows={3}
            />
          </div>

          {/* Total */}
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Total</span>
              <span className="text-2xl font-bold text-neon-blue">${calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-neon-blue hover:bg-neon-blue/90 text-black">
            {saving ? 'Creating...' : 'Create Booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
